import { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  SecondaryNavigationSelector,
  useSecondaryNavigationOverflow,
} from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { VALUATION_MARKET_DATE_TEST_IDS } from 'pages/ValuationsAllocation/ValuationMarketDate.testIds';

interface ValuationMarketDateSelectorProps {
  marketDate: string;
  onEdit: () => void;
}

const ValuationMarketDateSelector = ({ marketDate, onEdit }: ValuationMarketDateSelectorProps) => {
  const { zoomLevel } = useZoom();
  const { notifyNestedMenuClose, notifyNestedMenuOpen } = useSecondaryNavigationOverflow();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    notifyNestedMenuOpen();
    setAnchorEl(event.currentTarget);
  }, [notifyNestedMenuOpen]);

  const handleClose = useCallback(() => {
    notifyNestedMenuClose();
    setAnchorEl(null);
  }, [notifyNestedMenuClose]);

  const handleEdit = useCallback(() => {
    handleClose();
    onEdit();
  }, [handleClose, onEdit]);

  return (
    <>
      <SecondaryNavigationSelector
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="menu"
        data-testid={VALUATION_MARKET_DATE_TEST_IDS.versionSelector}
        label="Market Date"
        onClick={handleOpen}
        sx={{ maxWidth: 180 }}
        value={marketDate}
        valueTestId={VALUATION_MARKET_DATE_TEST_IDS.display}
        valueTone="brand"
      />
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        horizontalPlacement="right"
        menuMaxWidth={220}
        menuMinWidth={160}
        menuWidth="content"
        onClose={handleClose}
        open={Boolean(anchorEl)}
        zoomLevel={zoomLevel}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuItem
          data-testid={VALUATION_MARKET_DATE_TEST_IDS.editMarketDateMenuItem}
          icon={<EditOutlinedIcon />}
          onClick={handleEdit}>
          Edit Market Date
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
    </>
  );
};

export default ValuationMarketDateSelector;
